import { gql, useQuery } from '@apollo/client'
import * as d3 from 'd3-scale'
import * as _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import format from '../../../utils/format'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationMetricValuesByDateRangeNumber: {
    nodes: {
      startDate: string
      endDate: string
      metricData: Record<
        string,
        {
          name: string
          unit: 'DOLLAR'
          value: number
        }
      >
    }[]
  }
  latestItemPriceUnitOfMeasureName: {
    nodes: {
      name: string
      unitOfMeasureName: string
    }[]
  }
}

const query = gql`
  query BbbLatestItemPrice(
    $iStartDate: Date!
    $iEndDate: Date!
    $iLocationId: Int!
  ) {
    trendLocationMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: "last_x_weeks"
      iDateRangeNumber: 2
      iFilter: {
        location_ids: [$iLocationId]
        metric_groups: ["Expo Module - Item Latest Price"]
      }
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }

    latestItemPriceUnitOfMeasureName(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iLocationId: $iLocationId
    ) {
      nodes {
        name
        unitOfMeasureName
      }
    }
  }
`

export const bbbLatestItemPriceConfigs = {
  name: 'string',
  unitOfMeasureName: 'string',
  last2Week: 'price',
  last1Week: 'price',
  last0Week: 'price',
} as const

const useBbbLatestItemPrice = () => {
  const { groupFilter } = useGroupFilter()
  const { startDate, endDate } = useDateFilter()
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iLocationId: groupFilter?.ids[0],
    },
    skip: !groupFilter || !endDate,
  })

  return {
    data: useMemo((): IApiDataType => {
      if (!data) return null

      const lastWeeksData =
        data.trendLocationMetricValuesByDateRangeNumber.nodes
      const latestItemPriceUnitOfMeasureName =
        data.latestItemPriceUnitOfMeasureName.nodes

      const getHeatMap = (itemName: string, value: number) => {
        if (_.isNil(value)) return null

        const values = lastWeeksData.reduce((res, lwd) => {
          if (lwd.metricData && lwd.metricData[itemName].value)
            res.push(lwd.metricData[itemName].value)

          return res
        }, [] as number[])

        if (Math.min(...values) === Math.max(...values)) return 0

        const x = d3.scaleLinear(
          [0, 100],
          [Math.min(...values), Math.max(...values)],
        )

        return x.invert(value)
      }

      return Object.keys(lastWeeksData[0].metricData).map((key) =>
        lastWeeksData.reduce(
          (result, { metricData }, index) => {
            return {
              ...result,
              [`last${2 - index}Week`]: Object.values(
                format({ [key]: metricData[key] }),
              )[0],
              [`last${2 - index}WeekHeatMap`]: getHeatMap(
                key,
                metricData[key].value,
              ),
            }
          },
          {
            id: key,
            parentId: 'root',
            name: lastWeeksData[0].metricData[key].name,
            unitOfMeasureName: latestItemPriceUnitOfMeasureName.find(
              (l) => l.name === lastWeeksData[0].metricData[key].name,
            )?.unitOfMeasureName,
          },
        ),
      )
    }, [data]),
    loading,
  }
}

export default useBbbLatestItemPrice
